<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import {
  useStore,
  useStoreGetters,
  useMapGetter,
} from 'dashboard/composables/store';
import { useRoute } from 'vue-router';

import Button from 'dashboard/components-next/button/Button.vue';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import CampaignsToolbar from 'dashboard/components-next/Campaigns/CampaignsToolbar.vue';
import APICampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/APICampaign/APICampaignDialog.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import APICampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/APICampaignEmptyState.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import { useCampaignPagination } from 'dashboard/composables/useCampaignPagination';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();
const globalConfig = useMapGetter('globalConfig/get');
const route = useRoute();

const campaignPendingDelete = ref(null);
const [showAPICampaignDialog, toggleAPICampaignDialog] = useToggle();

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);

const confirmDeleteCampaignDialogRef = ref(null);

const APICampaigns = computed(() => getters['campaigns/getApiCampaigns'].value);

const hasNoAPICampaigns = computed(
  () => APICampaigns.value?.length === 0 && !isFetchingCampaigns.value
);

const slugify = value =>
  value
    ?.toString()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || '';

const buildReportParam = campaign => {
  if (!campaign?.id) return null;
  const slug = slugify(campaign.title);
  return slug ? `${campaign.id}-${slug}` : String(campaign.id);
};

const handleDelete = campaign => {
  campaignPendingDelete.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};

const refreshCampaigns = async () => {
  await store.dispatch('campaigns/get');
};

// API channel dynamic name
const apiChannelName = computed(
  () => globalConfig.value?.apiChannelName || t('INBOX_MGMT.CHANNELS.API')
);
const apiHeaderTitle = computed(() =>
  t('CAMPAIGN.API.DYNAMIC_HEADER_TITLE', { name: apiChannelName.value })
);

const resolveReportRoute = campaign => {
  if (!campaign?.id) return null;
  const param = buildReportParam(campaign);
  if (!param) return null;
  return {
    name: 'campaigns_api_report',
    params: {
      accountId: route.params.accountId,
      campaignParam: param,
    },
  };
};

// Search, Audience, Inbox and Status Filters
const searchValue = ref('');
const selectedLabels = ref([]);
const selectedInboxes = ref([]);
const filterOperator = ref('and');
const selectedStatuses = ref([]);

const stripHtml = s => (s || '').toString().replace(/<[^>]*>/g, ' ');
const contains = (hay, needle) =>
  (hay || '').toLowerCase().includes((needle || '').toLowerCase());

const normalizeStatus = s => {
  if (typeof s === 'number') {
    switch (s) {
      case 0:
        return 'active';
      case 1:
        return 'completed';
      case 2:
        return 'error';
      case 3:
        return 'running';
      default:
        return 'active';
    }
  }
  if (typeof s === 'string') {
    const v = s.toLowerCase();
    if (['active', 'completed', 'error', 'running'].includes(v)) return v;
  }
  return 'active';
};

const filteredAPICampaigns = computed(() => {
  const list = APICampaigns.value || [];
  const q = searchValue.value?.trim().toLowerCase();
  const selected = new Set(
    Array.isArray(selectedLabels.value)
      ? selectedLabels.value.map(labelId => String(labelId))
      : []
  );

  return list.filter(c => {
    const title = c.title || '';
    const message = stripHtml(c.message || '');
    const templateName = c.template_params?.name || '';
    const matchesSearch =
      !q ||
      contains(title, q) ||
      contains(message, q) ||
      contains(templateName, q);

    if (!matchesSearch) return false;

    const audienceIds = new Set(
      (c.audience || [])
        .filter(
          a => a && a.type === 'Label' && a.id !== null && a.id !== undefined
        )
        .map(a => String(a.id))
    );
    const hasLabelSelection = selected.size > 0;
    const labelMatch =
      !hasLabelSelection ||
      Array.from(selected).every(id => audienceIds.has(id));

    const inboxIds = Array.isArray(selectedInboxes.value)
      ? selectedInboxes.value.map(inboxId => String(inboxId))
      : [];
    const hasInboxSelection = inboxIds.length > 0;
    const inboxId = c.inbox?.id;
    const inboxMatch =
      !hasInboxSelection ||
      (inboxId != null && inboxIds.includes(String(inboxId)));

    // Status match (active/completed/error/running)
    const statuses = Array.isArray(selectedStatuses.value)
      ? selectedStatuses.value
      : [];
    const hasStatusSelection = statuses.length > 0;
    const statusValue = normalizeStatus(c.campaign_status ?? c.status);
    const statusMatch = !hasStatusSelection || statuses.includes(statusValue);

    if (filterOperator.value === 'and') {
      return labelMatch && inboxMatch && statusMatch;
    }
    const activeMatches = [];
    if (hasLabelSelection) activeMatches.push(labelMatch);
    if (hasInboxSelection) activeMatches.push(inboxMatch);
    if (hasStatusSelection) activeMatches.push(statusMatch);
    return activeMatches.length === 0 ? true : activeMatches.some(Boolean);
  });
});

const {
  currentPage,
  paginatedCampaigns: paginatedAPICampaigns,
  totalItems,
  shouldShowPagination,
  itemsPerPage,
  updateCurrentPage,
} = useCampaignPagination(filteredAPICampaigns);
</script>

<template>
  <CampaignLayout
    :header-title="apiHeaderTitle"
    :button-label="t('CAMPAIGN.API.NEW_CAMPAIGN')"
    @click="toggleAPICampaignDialog()"
    @close="toggleAPICampaignDialog(false)"
  >
    <CampaignsToolbar
      v-if="!hasNoAPICampaigns"
      v-model="selectedLabels"
      v-model:inboxes="selectedInboxes"
      v-model:statuses="selectedStatuses"
      v-model:operator="filterOperator"
      :search-value="searchValue"
      @search="val => (searchValue = val)"
    />
    <template #left-action>
      <Button
        icon="i-lucide-rotate-cw"
        size="sm"
        :is-loading="isFetchingCampaigns"
        :disabled="isFetchingCampaigns"
        @click="refreshCampaigns"
      >
        <span class="hidden sm:inline">{{ t('CAMPAIGN.REFRESH') }}</span>
      </Button>
    </template>
    <template #action>
      <APICampaignDialog
        v-if="showAPICampaignDialog"
        @close="toggleAPICampaignDialog(false)"
      />
    </template>
    <div
      v-if="isFetchingCampaigns"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <CampaignList
      v-else-if="!hasNoAPICampaigns && filteredAPICampaigns.length"
      :campaigns="paginatedAPICampaigns"
      :report-route-resolver="resolveReportRoute"
      @delete="handleDelete"
    />
    <div
      v-else-if="!hasNoAPICampaigns && !filteredAPICampaigns.length"
      class="flex items-center justify-center py-10"
    >
      <span class="text-base text-n-slate-11">
        {{ $t('CAMPAIGNS.LIST.NO_RESULTS') }}
      </span>
    </div>
    <APICampaignEmptyState
      v-else
      :title="
        t('CAMPAIGN.API.DYNAMIC_EMPTY_STATE_TITLE', {
          name: apiChannelName.value,
        })
      "
      :subtitle="t('CAMPAIGN.API.EMPTY_STATE.SUBTITLE')"
      class="pt-14"
    />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="campaignPendingDelete"
    />
    <template
      v-if="
        !hasNoAPICampaigns &&
        filteredAPICampaigns.length &&
        shouldShowPagination
      "
      #footer
    >
      <PaginationFooter
        :current-page="currentPage"
        :total-items="totalItems"
        :items-per-page="itemsPerPage"
        @update:current-page="updateCurrentPage"
      />
    </template>
  </CampaignLayout>
</template>
